<script setup lang="ts">
defineOptions({ inheritAttrs: false });

import { useI18n } from '@/i18n';
import { passwordRe, validatePassword } from '@/lib/validate';
import { Input, LayoutGap } from '@dabble/quillui';
import { computed, watch } from 'vue';
import Checkmark from './Checkmark.vue';

const props = defineProps<{
  modelValue: string;
  label?: string;
  name?: string;
  placeholder?: string;
  allowEmpty?: boolean;
  showRequirements?: boolean;
  class?: string;
  error?: boolean;
  errorMessage?: string;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void;
  (e: 'update:validPassword', value: true | string | undefined): void;
}>();

const { t } = useI18n();

const password = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
});

const validPassword = computed(() => {
  if (!password.value) return undefined;
  return validatePassword(password.value);
});

type RequirementKey = 'one_uppercase' | 'one_lowercase' | 'one_digit' | 'one_symbol' | 'characters';

const requirementDefs: { key: RequirementKey; labelKey: string }[] = [
  { key: 'one_uppercase', labelKey: 'rule_upper' },
  { key: 'one_lowercase', labelKey: 'rule_lower' },
  { key: 'one_digit', labelKey: 'rule_digit' },
  { key: 'one_symbol', labelKey: 'rule_symbol' },
  { key: 'characters', labelKey: 'rule_characters' },
];

const requirements = computed(() => ({
  one_uppercase: password.value.match(passwordRe[2].re) !== null,
  one_lowercase: password.value.match(passwordRe[1].re) !== null,
  one_digit: password.value.match(passwordRe[3].re) !== null,
  one_symbol: password.value.match(passwordRe[0].re) !== null,
  characters: password.value.match(passwordRe[4].re) !== null,
}));

watch(validPassword, (newValue) => {
  emit('update:validPassword', newValue);
});
</script>

<template>
  <div :class="props.class">
    <Input
      :id="name"
      v-model="password"
      v-bind="$attrs"
      :label="label || t('password')"
      :name="name"
      type="password"
      :placeholder="placeholder"
      class="password-input"
      :error="error"
      :error-message="errorMessage"
    />
    <LayoutGap
      v-if="showRequirements && (!allowEmpty || password)"
      direction="column"
      size="xxsmall"
      class="pw-requirements"
    >
      <LayoutGap
        v-for="row in requirementDefs"
        :key="row.key"
        direction="row"
        vertical-align="center"
        size="tiny"
        class="pw-requirement"
      >
        <Checkmark :checked="requirements[row.key]" />
        <span class="pw-requirement-text">{{ t(row.labelKey) }}</span>
      </LayoutGap>
    </LayoutGap>
  </div>
</template>

<style scoped>
.password-input {
  width: 100%;
}

.pw-requirements {
  margin-left: var(--size-tiny);
  margin-top: var(--font-size-default);
}

.pw-requirement {
  width: 100%;
}

.pw-requirement-text {
  font-size: var(--font-size-small);
  color: var(--color-text-secondary);
}
</style>
